import * as React from 'react'

import { Box, Flex, GridRow } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { AspectRatio } from '~ui/components/media/AspectRatio/AspectRatio'
import { Image } from '~ui/components/media/Image/Image'
import { Text } from '~ui/components/typography/Text/Text'
import { AllergenList } from '~ui/components/ware/Allergens/AllergenList'
import { TSingleSpace } from '~ui/styleGuide/spaces'

type TWareItem = {
  /**
   * item's unique identifier
   */
  id?: ID
  /**
   * url of a decorative image of the item
   */
  imageUrl?: Nullable<string>
  /**
   * name of the ware item
   */
  nameLabel: string
  /**
   * item description such as ingredients or a detailed info about the item
   */
  description: string
  /**
   * item price with the currency abbreviation
   */
  price: React.ReactNode
  /**
   * array of product's allergens with id, nameLabel (BE code) and allergen number (BE category)
   */
  allergens: PropsOf<typeof AllergenList>['allergens']
}

type TProps = NoChildren &
  SkeletonProps &
  TestingProps & {
    /**
     * data to be rendered
     */
    item: TWareItem
    /**
     * width or height of the square-shaped image
     */
    imageSize?: PropsOf<typeof Box>['width']
    /**
     * a function, that will be called when the item is clicked
     */
    onClick?: PropsOf<typeof Flex>['onClick']
    /**
     * any image element such as next/image, HTML img element or other, if no such element is provided, the chakra image is used with the item's imageUrl
     */
    customImageElement?: React.ReactNode
    /**
     * left padding of the ware item
     */
    leftPadding?: TSingleSpace
    /**
     * right padding of the ware item
     */
    rightPadding?: TSingleSpace
    /**
     * indicator of an action or any other special feature of the item (e.g. sold-out label)
     * positioned next to the price
     */
    priceBadge?: React.ReactNode
    /**
     * opacity of the item
     */
    opacity?: PropsOf<typeof Flex>['opacity']
    /**
     * translated label for allergens
     */
    allergenLabel?: PropsOf<typeof AllergenList>['label']
  }

export const WareItem: React.FC<TProps> = ({
  item,
  priceBadge,
  imageSize = `100px`,
  opacity,
  onClick,
  customImageElement,
  leftPadding = `none`,
  rightPadding = `none`,
  showSkeleton = false,
  dataTestId,
  allergenLabel = 'allergens',
}) => {
  /**
   * @returns provided customImageElement (such as next/image) OR Chakra Image with item's imageUrl OR null if both customImageElement and item.imageUrl are undefined
   */
  const renderImageElement = () => {
    if (customImageElement) {
      return customImageElement
    }

    if (item.imageUrl) {
      return <Image src={item.imageUrl} alt={`${item.nameLabel} image`} objectFit="cover" />
    }

    return null
  }

  const hasAllergens = item.allergens.length > 0
  const hasDescription = item.description.trim().length > 0

  return (
    <Flex
      onClick={showSkeleton ? undefined : onClick}
      justifyContent={`space-between`}
      padding={{ top: `none`, right: rightPadding, bottom: `none`, left: leftPadding }}
      cursor={onClick ? `pointer` : `auto`}
      opacity={opacity}
      gap={`small`}
      dataTestId={dataTestId}
    >
      <Stack direction={`column`} spacing={`extraSmall`} flexGrow={showSkeleton ? 1 : `unset`}>
        <Skeleton showSkeleton={showSkeleton} maxWidth={`20rem`}>
          <Text fontStyle={`bold`} fontSize={`m16`} truncateOnLines={2} wordBreak={`break-word`}>
            {item.nameLabel}
          </Text>
        </Skeleton>

        {hasDescription && (
          <Skeleton showSkeleton={showSkeleton}>
            <Text contrast={`low`} fontSize={`s14`} truncateOnLines={3} wordBreak={`break-word`}>
              {item.description}
            </Text>
          </Skeleton>
        )}

        {hasAllergens && (
          <Skeleton showSkeleton={showSkeleton} maxWidth={`8rem`}>
            {showSkeleton
              ? `allergens placeholder`
              : hasAllergens && (
                  <AllergenList
                    label={allergenLabel}
                    allergens={item.allergens}
                    fontSize={`s12`}
                    contrast={`low`}
                  />
                )}
          </Skeleton>
        )}

        <GridRow columnGap={`small`} justifyContent={`flex-start`} alignItems={`center`}>
          <Skeleton showSkeleton={showSkeleton} maxWidth={`5rem`}>
            <Text fontStyle={`boldSemi`} fontSize={`m16`}>
              {item.price}
            </Text>
          </Skeleton>
          {!showSkeleton && priceBadge}
        </GridRow>
      </Stack>
      {!showSkeleton && (customImageElement || item.imageUrl) && (
        <Box position={`relative`} flexGrow={0} flexShrink={0} flexBasis={imageSize}>
          <AspectRatio radius={`small`} maxWidth={`100%`}>
            {renderImageElement()}
          </AspectRatio>
        </Box>
      )}
      {showSkeleton && (
        <Skeleton>
          <Box width={imageSize} height={imageSize}></Box>
        </Skeleton>
      )}
    </Flex>
  )
}
